import React, { useState, useEffect, useContext } from "react";
// core components
import GridItem from "app/components/Grid/GridItem.js";
import GridContainer from "app/components/Grid/GridContainer.js";
import Button from "app/components/CustomButtons/Button.js";
import Card from "app/components/Card/Card.js";
import CardHeader from "app/components/Card/CardHeader.js";
import CardBody from "app/components/Card/CardBody.js";
import CardFooter from "app/components/Card/CardFooter.js";
import { Redirect } from 'react-router';
import axios from 'axios';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import DirectionsBoatFilledOutlinedIcon from '@mui/icons-material/DirectionsBoatFilledOutlined';
import FlightIcon from '@mui/icons-material/Flight';
import SailingOutlinedIcon from '@mui/icons-material/SailingOutlined';
import useWindowSize from 'app/hooks/useWindowSize.js'
import { DataGrid } from '@mui/x-data-grid';
import { LoginStatusContext } from "app/context/LoginStatusContext";

export default function NotificationSearch() {
    const [loading, setLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [acknowledgedNotifications, setAcknowledgedNotifications] = useState([]);
    const [notificationType, setNotificationType] = useState('New');
    const { setIsLoggedIn } = useContext(LoginStatusContext);
    
    useEffect(() => {
        async function validateUserSessionId() {
            try {
                await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/login/ValidateUserSessionId`)
                        .then(() => setIsLoggedIn(true));
                
            } catch (error) {
                setIsLoggedIn(false);
                window.location.replace(`${process.env.PUBLIC_URL}/login`);
            }
        }
        validateUserSessionId();
    }, [setIsLoggedIn]);

    const notTypes = ['New', 'Acknowledged'];

    const windowSize = useWindowSize();

    function ProcessDate(date) {
        var shortDate = date.substring(0, date.indexOf('T'));
        var [YYYY, MM, DD] = shortDate.split('-');
        var formattedShortDate = new Date(YYYY, MM - 1, DD);
        var userCountry = sessionStorage.getItem('_UserCountryCode');
        var requiredDate = null;

        if (userCountry === 'US' || userCountry === 'USA' || userCountry === 'CN' || userCountry === 'CNA') {
            requiredDate = formattedShortDate.toLocaleDateString('en-US');
            if (userCountry === 'CN' || userCountry === 'CNA') {
                requiredDate = formattedShortDate.toLocaleDateString('zh-Hans-CN');
            }
        }
        else {
            requiredDate = formattedShortDate.toLocaleDateString('en-GB');
        }

        return requiredDate;
    }

    useEffect(() => {
        getNotifications();
        setLoading(false);
    }, []);

    async function getNotifications() {
        return await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/notification/getnotifications`)
            .then((response) => {
                setNotifications(response.data);
            });
    }

    async function getAcknowledgedNotifications() {
        return await axios.get(`${process.env.REACT_APP_API_ENDPOINT}/api/notification/getacknowledgednotifications`)
            .then((response) => {
                setAcknowledgedNotifications(response.data);
            });
    }

    async function deleteNotification(selectedNotifications) {
        const formDataJsonString = JSON.stringify(selectedNotifications);
        return await axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}/api/notification/deletenotifications`,
                formDataJsonString,
                { params: {} }
            )
            .then(function (response) {
                let newNotifications = [...notifications].filter((notification) => {
                    return notification.isSelected !== true;
                });
                setNotifications(newNotifications);
            })
            .catch(function (error) {
            });
    }

    const handleNotificationTypes = (event, value) => {
        if (value === 'New') {
            setNotificationType('New');
            getNotifications();
        }
        else if (value === 'Acknowledged') {
            setNotificationType('Acknowledged');
            getAcknowledgedNotifications();
        }
    };

    const handleNotificationDelete = () => {
        let selectedNotifications = [...notifications].filter((notification) => {
            return notification.isSelected === true;
        });

        if (selectedNotifications.length > 0) {
            deleteNotification(selectedNotifications);
        }
    };

    const getIcon = (bookingTypeId) => {
        switch (bookingTypeId) {
            case 17:
                return <FlightIcon sx={{ fontSize: 60, backgroundColor: "#1DB35A", color: 'white' }} />
            case 18:
                return <DirectionsBoatFilledOutlinedIcon sx={{ fontSize: 60, backgroundColor: "#1DB35A", color: 'white' }} />
            case 19:
                return <FlightIcon sx={{ fontSize: 60, backgroundColor: "#2059A7", color: 'white' }} />
            case 20:
                return <DirectionsBoatFilledOutlinedIcon sx={{ fontSize: 60, backgroundColor: "#2059A7", color: 'white' }} />
            case 100:
                return <SailingOutlinedIcon sx={{ fontSize: 60, backgroundColor: "#2059A7", color: 'white' }} />
            default:
        }
    }

    const handleReferenceOnClick = (param) => {
        if (param.field === 'reference') {
            switch (param.row.bookingTypeId) {
                case 17:
                case 18:
                    sessionStorage.setItem('ShipmentID', param.row.referenceId);
                    return window.location.href = '/LeoSeaPortal/bookingdetails?shipmentId=' + param.row.referenceId;
                case 19:
                case 20:
                    sessionStorage.setItem('ShipmentID', param.row.referenceId);
                    return window.location.href = '/LeoSeaPortal/quotedetails?shipmentId=' + param.row.referenceId;
                case 100:
                    return window.location.href = '/LeoSeaPortal/customerorderdetails?customerOrderId=' + param.row.referenceId;
                default:
            }
        }
    }

    const notificationColumns = [
        {
            field: 'webPortalNotificationId',
            headerName: 'webPortalNotificationId',
            type: 'number',
            hide: true
        },
        {
            field: 'bookingTypeId',
            headerName: 'Booking Type',
            renderCell: (cellValues) => {
                return getIcon(cellValues.row.bookingTypeId);
            },
            minWidth: 100
        },
        {
            field: 'reference',
            headerName: 'Reference',
            minWidth: 200
        },
        {
            field: 'title',
            headerName: 'Title',
            minWidth: 250
        },
        {
            field: 'message',
            headerName: 'Message',
            minWidth: 300,
            flex: 1
        },
        {
            field: 'insertedDate',
            renderCell: (cellValues) => {
                return ProcessDate(cellValues.row.insertedDate);
            },
            headerName: 'Date Added',
            minWidth: 200
        }
    ];

    const handleRowSelection = (e) => {
        let newNotifications = [...notifications];

        newNotifications.forEach((notification) => {
            if (e.includes(notification.id)) {
                notification.isSelected = true;
            }
            else {
                notification.isSelected = false;
            }
        });

        setNotifications(newNotifications);
    };

    const renderNotificationTable = (notifications) => {
        return (
            <><div id="NotificationsTable">
                <GridItem xs={12}>
                    <div style={{ height: (windowSize.height - 270), width: '100%' }}>
                        <DataGrid
                            checkboxSelection
                            rows={notifications}
                            onSelectionModelChange={handleRowSelection}
                            columns={notificationColumns}
                            rowHeight={70}
                            pageSize={10}
                            onCellClick={handleReferenceOnClick}
                            sx={{
                                fontSize: 'small',
                                "& .MuiDataGrid-row:hover": {
                                    cursor: 'pointer'
                                }
                            }} />
                    </div>
                </GridItem>
            </div><>
                <CardFooter>
                    <Button style={{ backgroundColor: "#F78F35" }} type="submit" onClick={handleNotificationDelete}> Acknowledge </Button>
                </CardFooter></></>
        );
    }

    const renderAcknowledgementNotificationTable = (notifications) => {
        return (
            <GridItem xs={12}>
                <div style={{ height: (windowSize.height - 270), width: '100%' }}>
                    <DataGrid
                        rows={notifications}
                        columns={notificationColumns}
                        rowHeight={70}
                        pageSize={10}
                        onCellClick={handleReferenceOnClick}
                        sx={{
                            fontSize: 'small',
                            "& .MuiDataGrid-row:hover": {
                                cursor: 'pointer'
                            }
                        }} />
                </div>
            </GridItem>
        );
    }

    let contents = '';
    if (notificationType === 'New') {
        contents = loading
            ? <em>Loading...</em>
            : renderNotificationTable(notifications);
    }
    else {
        contents = loading
            ? <em>Loading...</em>
            : renderAcknowledgementNotificationTable(acknowledgedNotifications);
    }
    
    return (
        <div style={{ position: 'relative', zIndex: '0' }}>
            <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                    <Card>
                        <CardHeader style={{ backgroundColor: '#F78F35', color: '#FFF' }}>
                            <h4>Notifications</h4>
                        </CardHeader>
                        <GridItem xs={12} sm={12}>
                            <Autocomplete
                                size="small"
                                disablePortal
                                id="notTypes"
                                options={notTypes}
                                onChange={handleNotificationTypes}
                                defaultValue={notificationType}
                                sx={{ marginTop: 1 }}
                                renderInput={(params) => <TextField size="small" {...params} id="notTypes" name="notTypes" label="Notification Types" />}
                            />
                        </GridItem>
                        {contents}
                    </Card>
                </GridItem>
            </GridContainer>
        </div>
    );
}
